import React, { useState, useCallback, useRef, useEffect } from 'react'
import cls from '@UI/crypto_exchange/desktop/components/modals/ProblemGamblerModal/ProblemGamblerModal.module.scss'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import { asyncLogout } from '@Redux/slices/user/UserSlice'
import { formatDaysTitle, formatHoursTitle, formatMinutesTitle, formatMonthTitle } from '@App/helpers/time'
import Select from '@UI/crypto_exchange/desktop/components/modals/PersonalLimitModal/components/Select/Select'
import Input from '@UI/crypto_exchange/desktop/components/modals/PersonalLimitModal/components/Input/Input'
import Button from '@UI/crypto_exchange/desktop/components/modals/PersonalLimitModal/components/Button/Button'
import { useEditTimeoutsProblemGamblerMutation, useGetDocumentRequestsQuery } from '@Redux/api/user.api'

interface Props {
  changeStep: (step: number, params?: any) => void
  closeModal: () => void
}

interface TimeoutOption {
  value: any
  label: string
  data?: any
}

interface ValidationState {
  [key: string]: {
    status: boolean
    msg: string
    show_msg: boolean
  }
}

interface FormData {
  session_timeout: string
  reality_check_period: string
  exclude_period: string
  password: string
}

const Step3: React.FC<Props> = ({ changeStep, closeModal }) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const [loading, setLoading] = useState(false)
  const [validation, setValidation] = useState<ValidationState>({})
  const [focusField, setFocusField] = useState<string | null>(null)

  const { refetch: refetchDocumentRequests } = useGetDocumentRequestsQuery()
  const [editTimeoutsProblemGamblerMutation, { isLoading }] = useEditTimeoutsProblemGamblerMutation()

  const [formData, setFormData] = useState<FormData>({
    session_timeout: user?.session_timeout?.toString() || getTimeouts()[0].value.toString(),
    reality_check_period: user?.reality_check_period?.toString() || getRealityCheckPeriods()[0].value.toString(),
    exclude_period: user?.exclude_period?.toString() || getExcludePeriods()[0].value.toString(),
    password: '',
  })

  useEffect(() => {
    if (user) {
      setFormData({
        session_timeout: user.session_timeout?.toString() || getTimeouts()[0].value.toString(),
        reality_check_period: user.reality_check_period?.toString() || getRealityCheckPeriods()[0].value.toString(),
        exclude_period: user.exclude_period?.toString() || getExcludePeriods()[0].value.toString(),
        password: '',
      })
    }
  }, [user])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (validation[field]) {
      setValidation((prev) => ({
        ...prev,
        [field]: { ...prev[field], status: true, msg: '', show_msg: false },
      }))
    }
  }

  const handleBlur = (field: string) => {
    setFocusField(null)
    if (validation[field]) {
      setValidation((prev) => ({
        ...prev,
        [field]: { ...prev[field], show_msg: false },
      }))
    }
  }

  const handleFocus = (e: any, field?: string) => {
    const fieldName =
      field !== 'password'
        ? field || (e.target as HTMLSelectElement).name
        : field || (e.target as HTMLInputElement).name
    setFocusField(fieldName)

    if (validation[fieldName] && !validation[fieldName].status) {
      setValidation((prev: any) => ({
        ...prev,
        [fieldName]: { ...prev[fieldName], show_msg: true },
      }))
    }
  }

  const getValidationClass = (field: string) => {
    if (validation[field]) {
      return validation[field].status ? '' : cls.error
    }
    return ''
  }

  const getErrorStatus = (field: string) => {
    return validation[field] && validation[field].show_msg
  }

  const getErrorMessage = (field: string) => {
    return validation[field] && validation[field].show_msg ? validation[field].msg : ''
  }

  const handleValidationError = (error: any) => {
    const newValidation: ValidationState = {}

    if (error.data) {
      Object.keys(error.data).forEach((key) => {
        if (
          key === 'session_timeout' ||
          key === 'reality_check_period' ||
          key === 'exclude_period' ||
          key === 'password'
        ) {
          newValidation[key] = {
            status: false,
            msg: error.data.title_params ? error.data[key] + ' ' + error.data.title_params[0] : error.data[key],
            show_msg: true,
          }

          if (key === 'password') {
            setFormData((prev) => ({ ...prev, password: '' }))
            setFocusField('password')
          }
        }
      })
    }

    if (Object.keys(newValidation).length === 0) {
      newValidation.general = {
        status: false,
        msg: error.message || 'Failed to save settings. Please try again.',
        show_msg: true,
      }
    }

    setValidation(newValidation)
  }

  const isSaveEnabled = useCallback(() => {
    if (!user) return false

    const fields = ['reality_check_period', 'session_timeout', 'exclude_period']
    let hasChanges = false

    for (const field of fields) {
      const currentValue = user[field as keyof typeof user]
      const newValue = formData[field as keyof typeof formData]

      if ((currentValue || newValue) && currentValue?.toString() !== newValue) {
        hasChanges = true
        break
      }
    }

    if (!hasChanges) return false

    return formData.password.trim().length > 0
  }, [user, formData])

  const handleSave = async () => {
    if (!formData.password.trim()) {
      setValidation({
        password: {
          status: false,
          msg: 'Password is required',
          show_msg: true,
        },
      })
      setFocusField('password')
      return
    }

    setLoading(true)
    setValidation({})

    try {
      const requestData = {
        session_timeout: Number(formData.session_timeout),
        reality_check_period: Number(formData.reality_check_period),
        exclude_period: formData.exclude_period === 'none' ? null : Number(formData.exclude_period),
        password: formData.password,
        type: 'preferences_problem_gambler',
      }

      const result = await editTimeoutsProblemGamblerMutation(requestData).unwrap()

      if (result.status) {
        await refetchDocumentRequests()

        setFormData((prev) => ({ ...prev, password: '' }))
        setValidation({})

        changeStep(4)

        setTimeout(() => {
          dispatch(asyncLogout())
          closeModal()
        }, 2000)
      } else {
        handleValidationError(result)
      }
    } catch (error: any) {
      handleValidationError(error)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && formData.password.trim()) {
      e.preventDefault()
      handleSave()
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !loading && formData.password.trim()) {
        handleSave()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [loading, formData])

  const renderSaveButton = () => (
    <Button
      clickButton={handleSave}
      buttonClass={`${cls.button} ${cls.primaryButton}`}
      disabled={!isSaveEnabled() || loading}
      loading={loading}
      className={`${cls.okButton} ${!isSaveEnabled() || loading ? cls.disabled : ''}`}
    >
      Save
    </Button>
  )

  function getTimeouts(): TimeoutOption[] {
    return [
      { label: 'Disabled', value: 0 },
      { label: formatMinutesTitle(30), value: 30 },
      { label: formatHoursTitle(1), value: 60 },
      { label: formatHoursTitle(2), value: 60 * 2 },
      { label: formatHoursTitle(3), value: 60 * 3 },
      { label: formatHoursTitle(4), value: 60 * 4 },
      { label: formatHoursTitle(5), value: 60 * 5 },
    ]
  }

  function getExcludePeriods(): TimeoutOption[] {
    return [
      { label: 'Please select', value: 'none' },
      { label: formatHoursTitle(24), value: '1' },
      { label: formatHoursTitle(48), value: '2' },
      { label: formatDaysTitle(7), value: '7' },
      { label: formatDaysTitle(30), value: '30' },
      { label: formatMonthTitle(6), value: '180' },
      { label: formatMonthTitle(12), value: '360' },
      { label: 'Completely', value: 'completely' },
    ]
  }

  function getRealityCheckPeriods(): TimeoutOption[] {
    return [
      { label: 'No check', value: 0 },
      { label: formatMinutesTitle(10), value: 10 },
      { label: formatMinutesTitle(20), value: 20 },
      { label: formatMinutesTitle(30), value: 30 },
      { label: formatHoursTitle(1), value: 60 },
      { label: formatHoursTitle(2), value: 60 * 2 },
      { label: formatHoursTitle(3), value: 60 * 3 },
      { label: formatHoursTitle(4), value: 60 * 4 },
      { label: formatHoursTitle(6), value: 60 * 6 },
      { label: formatHoursTitle(8), value: 60 * 8 },
    ]
  }

  return (
    <div className={`${cls.step} ${cls.realityCheck}`}>
      <div className={cls.modalHeader}>
        <div className={cls.title}>{'You might be at risk'}</div>
      </div>

      <div className={cls.description}>
        To facilitate self-control when it comes to playing time, we would require a session timeout to be defined:
      </div>

      <div className={cls.formGroup}>
        <Select
          value={formData.session_timeout}
          title={'Session timeout'}
          name="session_timeout"
          options={getTimeouts()}
          onChangeHandler={handleInputChange}
          onBlurHandler={() => handleBlur('session_timeout')}
          onFocus={handleFocus}
          error={getErrorStatus('session_timeout')}
          errorMessage={getErrorMessage('session_timeout')}
          focus={focusField === 'session_timeout'}
          className={getValidationClass('session_timeout')}
        />
      </div>

      <div className={cls.description}>
        In addition, a reality check tool can be attached to your account. This tool shows a message that contains your
        win/loss history; it appears on the screen after you have played the selected amount of time.
      </div>

      <div className={cls.formGroup}>
        <Select
          value={formData.reality_check_period}
          title={'Reality Check'}
          name="reality_check_period"
          options={getRealityCheckPeriods()}
          onChangeHandler={handleInputChange}
          onBlurHandler={() => handleBlur('reality_check_period')}
          onFocus={handleFocus}
          error={getErrorStatus('reality_check_period')}
          errorMessage={getErrorMessage('reality_check_period')}
          focus={focusField === 'reality_check_period'}
          className={getValidationClass('reality_check_period')}
        />
      </div>

      <div className={cls.description}>
        CFD customers can restrict themselves entirely from games or exclude their account for a specific period of
        time. During the time-out, the customer cannot, for any reason, access their account at CFD. Once the period is
        finished, the player can come back to the site. CFD does everything in its power to prevent a customer from
        opening a new account during the time-out period.
      </div>

      <div className={cls.formGroup}>
        <Select
          value={formData.exclude_period}
          name="exclude_period"
          title={'Time-out'}
          options={getExcludePeriods()}
          onChangeHandler={handleInputChange}
          onBlurHandler={() => handleBlur('exclude_period')}
          onFocus={handleFocus}
          error={getErrorStatus('exclude_period')}
          errorMessage={getErrorMessage('exclude_period')}
          focus={focusField === 'exclude_period'}
          className={getValidationClass('exclude_period')}
        />
      </div>

      <div className={cls.clearfix} />

      {validation.general && validation.general.show_msg && (
        <div className={cls.errorMessage}>{validation.general.msg}</div>
      )}

      <div className={cls.saveSection}>
        <div className={cls.formGroup}>
          <Input
            value={formData.password}
            name="password"
            title={'Password'}
            type="password"
            placeholder=""
            onChangeHandler={handleInputChange}
            onBlurHandler={() => handleBlur('password')}
            onFocus={handleFocus}
            error={getErrorStatus('password')}
            errorMessage={getErrorMessage('password')}
            focus={focusField === 'password'}
            className={getValidationClass('password')}
            onKeyDown={handleKeyDown}
            autoComplete="current-password"
            required
          />
        </div>
        {renderSaveButton()}
      </div>
    </div>
  )
}

export default Step3
